<template>
  <div class="preview-deck">
    <div class="main-info">
      <Breadcrumb :breadcrumb="projectBreadcrumb" class="flash-breadcrumb" />
      <div class="holder-info">
        <div class="holder-title">
          <h1 class="title h2">
            {{ deck.title }}
          </h1>
          <div class="info">
            <span class="author">Author: <span class="bold">{{
              deck.author }}</span></span>
            <span class="dot" />
            <span class="cards">Cards: <span class="bold">{{ deck.cardCount }}</span></span>
          </div>
        </div>
        <div class="subtitle-wrapper mobile">
          <p class="subtitle" :class="{ overlay: symbolLengthCheck && !seemore }">
            {{ deck.description }}
          </p>
          <div v-if="symbolLengthCheck" class="see-more">
            <a class="link-accent" @click="seeMore($event)">
              <span>See {{ seemore ? 'Less' : 'More' }}</span>
              <ArrowDown />
            </a>
          </div>
        </div>
        <div class="buttons">
          <nuxt-link
            to="/flashcards/" class="btn-text go-back-btn"
            :class="{ 'btn-outline-dark-light-4': $store.state.layout === 'mobile' }"
          >
            Go Back
          </nuxt-link>
          <nuxt-link
            :to="!deck.status ? `/flashcards/${deck.slug}/review/` : `/flashcards/${deck.slug}/study/`"
            class="btn-primary"
          >
            {{ !deck.status ? 'Review deck' : 'Study deck' }}
          </nuxt-link>
        </div>
      </div>
      <div class="subtitle-wrapper desktop">
        <p class="subtitle" :class="{ overlay: symbolLengthCheck && !seemore }">
          {{ deck.description }}
        </p>
        <div v-if="symbolLengthCheck" class="see-more">
          <a class="link-accent" @click="seeMore($event)">
            <span>See {{ seemore ? 'Less' : 'More' }}</span>
            <ArrowDown />
          </a>
        </div>
      </div>
    </div>
    <div v-if="showResults" class="result-cards row">
      <div class="col col-8">
        <ResultCard
          :test-score="deck.results.testScore"
          :study-score="deck.results.studyScore"
          :status="deck.status"
          :deck="deck"
        />
      </div>
      <div class="col col-3 md-4">
        <NextCard :review="!!$route.query.review" :data="nextCardData" />
      </div>
    </div>
    <DeckTerms :review="!!$route.query.review" :deck="deck" />
  </div>
</template>

<script>
import Breadcrumb from '../components/Breadcrumb';
import ResultCard from './Results/Section';
import NextCard from './NextCard';
import DeckTerms from './DeckTerms';
import ArrowDown from '../assets/svg/carret-down-flashcards.svg?vue-component';

export default {
  components: {
    Breadcrumb,
    ResultCard,
    NextCard,
    DeckTerms,
    ArrowDown,
  },

  props: {
    info: { type: Object, required: true, default: () => ({}) },
  },

  data() {
    return {
      deck: this.info,
      nextCardData: this.info.results,
      seemore: false,
    };
  },
  computed: {
    projectBreadcrumb() {
      return [
        {
          slug: '/flashcards/',
          name: 'All Decks',
        },
        {
          slug: this.$route.query.review ? `/flashcards/${this.deck.slug}/?review=1` : `/flashcards/${this.deck.slug}/`,
          name: this.deck.title,
        },
        {
          slug: '',
          name: 'Preview',
        },
      ];
    },
    showResults() {
      return this.$route.query.review || (this.deck.status && (Object.keys(this.deck.results.studyScore).length !== 0 || Object.keys(this.deck.results.testScore).length !== 0));
    },
    symbolLengthCheck() {
      if (this.$store.state.layout === 'desktop') {
        return this.deck.description.length > 500;
      }
      return this.deck.description.length > 300;
    },
  },
  mounted() {
    this.$nextTick(() => this.renderMathJax());

    this.nextCardData.title = this.deck.title;
  },
  methods: {
    renderMathJax() {
      if (window.MathJax) {
        window.MathJax.Hub.Config({
          tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            processEscapes: true,
          },
          'HTML-CSS': {
            linebreaks: { automatic: true, width: 'container' },
          },
          SVG: {
            linebreaks: { width: '10000px' },
          },
          jax: ['input/TeX', 'output/SVG'],
        });
        window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub]);
      }
    },
    seeMore(event) {
      this.seemore = !this.seemore;
      const elem = event.target.closest('.link-accent').parentElement.previousElementSibling;
      if (this.seemore) {
        elem.style.height = `${elem.scrollHeight}px`;
        setTimeout(() => {
          elem.classList.remove('overlay');
        }, 300);
      } else {
        elem.style.height = null;
      }
    },
  },
};
</script>

<style lang="scss">
.preview-deck {
  padding-top: var(--spacing-140);
  padding-bottom: var(--spacing-120);

  .main-info {
    top: 68px;
    z-index: 2;

    .holder-info {
      margin-top: var(--spacing-12);
      display: flex;
      justify-content: space-between;

      .holder-title {
        .title {
          margin-bottom: var(--spacing-12);
        }

        .info {
          color: var(--main-dark-light-3);
          font-size: 18px;
          line-height: 140%;

          .bold {
            color: var(--main-dark-light-2);
            font-weight: 500;
          }

          .dot {
            &:before {
              content: '';
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: var(--main-dark-light-3);
              display: inline-block;
              vertical-align: middle;
              margin: 0 var(--spacing-8);
            }
          }
        }
      }

      .buttons {
        display: flex;
        height: 62px;

        button {
          font-weight: 600;
          padding: 0;
        }

        .btn-text {
          color: var(--main-light-shade-4);
          margin-right: var(--spacing-30);
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            color: var(--main-dark-light-4);
          }

          &:active {
            color: var(--main-dark-light-3);
          }

          &.go-back-btn {
            white-space: nowrap;
          }
        }

        .btn-primary {
          width: 150px;
          height: 62px;
          padding-left: 0;
          padding-right: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }

    .subtitle-wrapper {
      margin-top: var(--spacing-20);
      padding-bottom: var(--spacing-16);
      border-bottom: 1px solid var(--main-light-shade-3);

      &.mobile {
        display: none;
      }

      .subtitle {
        margin: 0;
        transition: height var(--duration) ease-out;
        color: var(--main-dark-light-4);
        font-size: 18px;
        line-height: 110%;
        overflow: hidden;
        white-space: pre-line;

        &.overlay {
          height: 80px;
          position: relative;

          &:before {
            content: "";
            position: absolute;
            bottom: 0;
            background: linear-gradient(180deg, rgba(255, 255, 255, 0) 22.02%, #FAFAFB 84.72%);
            width: 100%;
            height: 36px;
            z-index: 1;
          }
        }

        &:not(.overlay) {
          + .see-more {
            margin-top: var(--spacing-16);

            svg {
              transform: rotate(180deg);
            }
          }
        }
      }

      .see-more {
        display: flex;
        justify-content: center;
        margin-top: 3px;

        .link-accent {
          display: flex;
          align-items: center;
          gap: var(--spacing-8);

          span {
            font-size: 18px;
            font-weight: 500;
            line-height: 140%;
          }
        }
      }
    }
  }

  .result-cards {
    margin-top: var(--spacing-30);
    margin-bottom: var(--spacing-60);

    .col-3 {
      display: flex;
      align-items: flex-end;
    }
  }

  .pagination {
    margin-top: var(--spacing-40);
    margin-bottom: 0;
  }
}

@include bp($bp-laptop) {
  .preview-deck {
    .pagination {
      margin-top: var(--spacing-20);
    }
  }
}

@include bp($bp-tablet) {
  .preview-deck .main-info .holder-info .buttons {
    .btn-text,
    .btn-primary {
      max-width: 375px;
    }
  }
}

@include bp($bp-mobile) {
  .preview-deck {
    padding-top: 108px;

    .main-info {
      position: unset;

      .subtitle-wrapper {
        border-bottom: unset;
        margin: var(--spacing-12) 0;
        padding-bottom: 0;

        &.desktop {
          display: none;
        }

        &.mobile {
          display: block;
        }
      }

      .holder-info {
        flex-direction: column;

        .buttons {
          height: unset;
          display: flex;
          flex-direction: column-reverse;
          border-bottom: 1px solid var(--main-light-shade-4);
          padding-bottom: var(--spacing-16);

          .btn-text {
            width: 100%;
            height: 52px;
            margin-right: 0;
            margin-top: var(--spacing-16);
          }

          .btn-primary {
            width: 100%;
            height: 52px;
          }
        }

        .holder-title {
          .title {
            font-size: 24px;
            margin-bottom: var(--spacing-6);
          }

          .info {
            font-size: 16px;
            font-weight: 500;
            line-height: 110%;
            color: var(--main-dark);
          }
        }
      }
    }

    .result-cards {
      margin-bottom: var(--spacing-50);

      .exam-result {
        .holder-result {
          .subtitle {
            margin-bottom: 20px;
          }

          .result p {
            text-align: center;
          }

          .answers.mobile {
            div {
              width: unset;
              margin-bottom: 0;

              span {
                font-size: 14px;
              }

              .corrrect,
              .incorrect {
                font-size: 12px;
              }
            }
          }
        }

        .btn-holder {
          justify-content: center;

          .btn-text {
            font-size: 16px;
          }
        }
      }

      .deck-progress {
        .holder-result {
          .title {
            text-align: center;
            margin-bottom: var(--spacing-lg);
          }

          .answers.mobile {
            div {
              margin-bottom: 0;

              span {
                font-size: 14px;
              }
            }
          }
        }

        .btn-holder {
          margin-top: var(--spacing-30);
          justify-content: center;

          .btn-text {
            font-size: 16px;
          }
        }
      }

      .next-card {
        margin-top: var(--spacing-30);
      }
    }
  }
}
</style>
